import React, { useState } from 'react';
import { useTasks } from '@/context/TaskContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import CurrentTaskCard from './CurrentTaskCard';
import SequenceIndicator from './SequenceIndicator';

const CONGRATS_MESSAGES = [
  "Great job!",
  "Awesome work!",
  "Well done!",
  "Fantastic!",
  "Nicely done!",
  "You did it!",
];

interface FocusAreaCardProps {
  sessions: number;
  currentSession: number;
  currentType: 'pomodoro' | 'shortBreak' | 'longBreak';
  isMobile?: boolean;
  activeIndex?: number;
  onOpenTaskList: () => void;
  currentTime: Date;
  timerSize?: number;
}

const FocusAreaCard: React.FC<FocusAreaCardProps> = ({
  sessions,
  currentSession,
  currentType,
  isMobile = false,
  activeIndex = -1,
  onOpenTaskList,
  currentTime,
  timerSize,
}) => {
  const { activeTask, updateTaskStatus, setActiveTask } = useTasks();
  const { user, profile } = useAuth();
  const [showCongrats, setShowCongrats] = useState(false);
  const [congratsMsgIndex, setCongratsMsgIndex] = useState<number>(() =>
    Math.floor(Math.random() * CONGRATS_MESSAGES.length)
  );

  // Helper function to get user's display name
  const getUserDisplayName = () => {
    if (profile?.name) return profile.name;
    if (user?.user_metadata?.first_name && user?.user_metadata?.last_name) {
      return `${user.user_metadata.first_name} ${user.user_metadata.last_name}`;
    }
    if (user?.user_metadata?.name) return user.user_metadata.name;
    if (user?.user_metadata?.full_name) return user.user_metadata.full_name;
    return null;
  };

  // Helper function to get time-aware greeting
  const getTimeAwareGreeting = () => {
    const hour = currentTime.getHours();
    const userName = getUserDisplayName();
    const nameText = userName ? `, ${userName}` : '';
    if (hour >= 5 && hour < 12) {
      return {
        greeting: `Good morning${nameText}`,
        subtext: "What are you working on today?"
      };
    } else if (hour >= 12 && hour < 18) {
      return {
        greeting: `Good afternoon${nameText}`,
        subtext: "What are you working on this afternoon?"
      };
    } else if (hour >= 18 && hour < 22) {
      return {
        greeting: `Good evening${nameText}`,
        subtext: "What would you like to focus on?"
      };
    } else {
      return {
        greeting: userName ? `Burning the midnight oil, ${userName}?` : "Burning the midnight oil?",
        subtext: "What would you like to focus on?"
      };
    }
  };

  const { greeting, subtext } = getTimeAwareGreeting();
  const cardWidth = timerSize ? timerSize : (isMobile ? Math.min(window.innerWidth - 50, 350) : 525);

  // Handle clicking "Done"
  const handleDoneClick = async () => {
    if (activeTask) {
      await updateTaskStatus(activeTask.id, 'done');
      setCongratsMsgIndex(Math.floor(Math.random() * CONGRATS_MESSAGES.length));
      setShowCongrats(true);
      setActiveTask && setActiveTask(null);
    }
  };

  // --- CONGRATULATIONS CARD ---
  if (showCongrats) {
    return (
      <div
        className={`w-full mx-auto ${isMobile ? 'mx-[25px]' : 'px-2'}`}
        style={{
          maxWidth: cardWidth,
          width: cardWidth,
        }}
      >
        <div
          className="glass-panel rounded-xl backdrop-blur-md shadow-lg border-0 flex flex-col items-center px-4 py-6"
          style={{
            overflowX: 'hidden',
            overflowY: 'visible',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="mb-2 text-center w-full px-1">
            <h2 className="text-xl text-white font-light flex items-center justify-center gap-1 mb-1">
              {CONGRATS_MESSAGES[congratsMsgIndex]}
              <span role="img" aria-label="clap" className="text-2xl ml-2">👏</span>
            </h2>
            <p className="text-white/90 font-light mb-3 leading-snug text-base">
              You’ve completed your task.<br />
              Ready for another round?
            </p>
          </div>
          <Button
            onClick={() => { setShowCongrats(false); onOpenTaskList(); }}
            className="bg-black/30 hover:bg-black/40 shadow-sm text-white h-11 px-6 text-sm rounded-full"
          >
            Select another task
          </Button>
          <div className="w-full px-1 mt-4">
            <SequenceIndicator
              sessions={sessions}
              currentSession={currentSession}
              currentType={currentType}
              isMobile={isMobile}
              activeIndex={activeIndex}
            />
          </div>
        </div>
      </div>
    );
  }

  // --- DEFAULT FOCUS CARD ---
  return (
    <div
      className={`w-full mx-auto ${isMobile ? 'mx-[25px]' : 'px-2'}`}
      style={{
        maxWidth: cardWidth,
        width: cardWidth,
      }}
    >
      <div
        className="glass-panel rounded-xl backdrop-blur-md shadow-lg border-0"
        style={{
          overflowX: 'hidden',
          overflowY: 'visible',
        }}
      >
        <div>
          {/* No Active Task */}
          {!activeTask && (
            <div className="px-5 pt-5 pb-0 text-center">
              <div className="mb-3">
                <h2 className="text-xl text-white font-light whitespace-nowrap overflow-hidden text-ellipsis">
                  {greeting}
                </h2>
                <p className="text-sm text-white/90 font-light mt-1">
                  {subtext}
                </p>
              </div>
              <Button
                onClick={onOpenTaskList}
                className="bg-black/30 hover:bg-black/40 shadow-sm text-white h-12 px-6 text-sm rounded-full"
              >
                Select a task
              </Button>
            </div>
          )}

          {/* Active Task */}
          {activeTask && (
            <div className="px-5 pt-2 pb-0.5">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1"></div>
                <div className="flex gap-4 text-xs">
                  <button
                    onClick={onOpenTaskList}
                    className="text-white/70 hover:text-white hover:underline transition-colors"
                  >
                    Change
                  </button>
                  <button
                    onClick={handleDoneClick}
                    className="text-white/70 hover:text-green-300 hover:underline transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
              <CurrentTaskCard task={activeTask} />
            </div>
          )}
        </div>

        {/* SequenceIndicator: more vertical space with py-4 */}
        <div className="w-full px-5 py-4 mb-2">
          <SequenceIndicator
            sessions={sessions}
            currentSession={currentSession}
            currentType={currentType}
            isMobile={isMobile}
            activeIndex={activeIndex}
          />
        </div>
      </div>
    </div>
  );
};

export default FocusAreaCard;
